import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"

/**
 * Search Facebook pages / Instagram places by query string.
 * GET /api/v1/facebook/pages/search?q={query}
 * GET /api/v1/facebook/pages/search?q={query}&location=true — Instagram Location row (CampaignLocationField)
 * @param {string|{ query?: string, forLocation?: boolean }} arg
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export const searchFacebookPages = createAsyncThunk(
  "facebookPages/search",
  async (arg, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      const forLocation =
        arg != null && typeof arg === "object" && arg.forLocation === true
      const q =
        typeof arg === "string"
          ? arg.trim()
          : String(arg?.query ?? "").trim()
      const locationParam = forLocation ? "&location=true" : ""
      const data = await get(
        `facebook/pages/search?q=${encodeURIComponent(q)}${locationParam}`,
      )

      if (Array.isArray(data)) return data
      if (data?.data && Array.isArray(data.data)) return data.data
      if (data?.pages && Array.isArray(data.pages)) return data.pages
      return []
    } catch (err) {
      console.error("Facebook pages search error:", err)
      return rejectWithValue(err?.message || "Failed to search Facebook pages")
    }
  }
)

const initialState = {
  pages: [],
  lastQuery: "",
  status: "idle",
  error: null,
}

const facebookPagesSlice = createSlice({
  name: "facebookPages",
  initialState,
  reducers: {
    clearFacebookPages: (state) => {
      state.pages = []
      state.lastQuery = ""
      state.status = "idle"
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchFacebookPages.pending, (state, action) => {
        state.status = "loading"
        state.error = null
        state.lastQuery = action.meta?.arg ?? ""
      })
      .addCase(searchFacebookPages.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.pages = Array.isArray(action.payload) ? action.payload : []
        state.error = null
      })
      .addCase(searchFacebookPages.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to search Facebook pages"
        state.pages = []
      })
  },
})

export const { clearFacebookPages } = facebookPagesSlice.actions
export default facebookPagesSlice.reducer
